class AffiliateFlow {
  static _fetchPromise = null;
  static _affiliateData = null;

  static KEYS = {
    AFFILIATE_LOCATIONS: 'affiliate_locations',
    SHOP_ACCESS_GRANTED: 'shop_access_granted',
    PREVIOUS_ORDER_ATTRIBUTE: 'previous_order_attribute',

    // URL parameter and dataset keys
    PARAM_QR_CODE_ID: 'qr_code_id',
    PARAM_LOCATION_ID: 'location_id',
    PARAM_REFERRER: 'referrer',
    PARAM_TRACKING_SOURCE: 'tracking_source',
    PARAM_PRODUCT_ID: 'product_id',
    PARAM_VARIANT_ID: 'variant_id',
    PARAM_SELLING_PLAN_ID: 'selling_plan_id',

    // Tracking source values
    SOURCE_ORGANIC: 'organic',
    SOURCE_UNITY_QR_CODE_SCAN: 'unity_qr_code_scan',
    SOURCE_AFFILIATE: 'affiliate_referral',
    SOURCE_MOBILE: 'mobile_app',
  };

  static init() {
    const run = async () => {
      await this.syncReferralParamsToCart(); // Ensure any referral parameters are synced to the cart attributes and header data attributes on page load
      await this.handleReferralFlow(); // This sets _affiliateData which is needed for the banner content

      this.ensureAffiliateLocationUI(); // Set up the affiliate banner in the header if it's not already present
      this.updateAffiliateBannerContent(); // Update banner content after fetching affiliate data

      this.setupATCButtons(); // Set up event listeners for "Add to Cart" buttons in affiliate flow
      this.reapplyPreviousOrderAttributes(); // Reapply previous order attributes to maintain affiliate context across sessions
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  // Sync referral parameters (affiliate ID, location ID, QR code ID) to cart attributes on page load
  static async syncReferralParamsToCart() {
    const {
      PARAM_QR_CODE_ID,
      PARAM_LOCATION_ID,
      PARAM_REFERRER,
      PARAM_TRACKING_SOURCE,
      SOURCE_ORGANIC,
      SOURCE_UNITY_QR_CODE_SCAN,
      SOURCE_AFFILIATE,
      SOURCE_MOBILE,
    } = this.KEYS;

    const qrCodeId = this.params.get(PARAM_QR_CODE_ID);
    const locationId = this.params.get(PARAM_LOCATION_ID);
    const affiliateId = this.params.get(PARAM_REFERRER);

    if (!(qrCodeId || locationId || affiliateId)) return;

    // Clear any previous post-order attributes to avoid accidentally applying them to the wrong session
    sessionStorage.removeItem(this.KEYS.PREVIOUS_ORDER_ATTRIBUTE);

    // Determine tracking source based on combination of parameters
    let trackingSource = SOURCE_ORGANIC;

    if (qrCodeId && locationId) {
      trackingSource = SOURCE_UNITY_QR_CODE_SCAN;
    } else if (affiliateId && !qrCodeId) {
      trackingSource = SOURCE_AFFILIATE;
    } else if (locationId && !affiliateId && !qrCodeId) {
      trackingSource = SOURCE_MOBILE;
    }

    const attributes = {
      [PARAM_QR_CODE_ID]: qrCodeId || null,
      [PARAM_LOCATION_ID]: locationId || null,
      [PARAM_REFERRER]: affiliateId || null,
      [PARAM_TRACKING_SOURCE]: trackingSource,
    };

    const updateCartItemPayload = SHOPIFY_API.UPDATE_CART_ITEM;
    updateCartItemPayload.body = JSON.stringify({ attributes, sections: 'header' });

    const updateCartResponse = await fetchData(updateCartItemPayload);
    const updateCartResult = await updateCartResponse.json();

    if (updateCartResponse.ok) {
      this.applyHeaderDataAttributes(updateCartResult.sections?.header);
    } else {
      console.error('[AffiliateFlow] Failed to update cart with referral params:', updateCartResult);
    }
  }

  static applyHeaderDataAttributes(sectionHtml) {
    if (!sectionHtml) return;

    const headerElement = document.querySelector('header');
    if (!headerElement) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newHeader = doc.querySelector('header');
    if (!newHeader) return;

    // Mirror every data-* attribute from the server-rendered header to the live one
    Object.entries(newHeader.dataset).forEach(([key, value]) => {
      headerElement.dataset[key] = value;
    });
  }

  // Main entry point - determines if user is a referred/affiliate user and applies necessary UI changes and redirects
  static async handleReferralFlow() {
    try {
      const { SOURCE_MOBILE, SOURCE_UNITY_QR_CODE_SCAN, SOURCE_ORGANIC, SOURCE_AFFILIATE } = this.KEYS;

      const affiliateId = this.getAffiliateId();
      const source = this.getSource();
      const locationId = this.getLocationId();

      const hasDirectAccess = source === SOURCE_MOBILE || source === SOURCE_UNITY_QR_CODE_SCAN;
      const isOrganicWithLocation = source === SOURCE_ORGANIC && !!locationId;

      let data = null;
      if (affiliateId) {
        data = await this.fetchAffiliateData(affiliateId);
      }

      this._affiliateData = data;

      const locationCount = data?.locationCount ?? 0;

      // Referral users with a location ID but more than 1 location should see the form but not the locations section
      const isReferralWithMultipleLocations = source === SOURCE_AFFILIATE && !!locationId && locationCount > 1;

      this.applyAffiliateUI({ hasDirectAccess, locationCount, isOrganicWithLocation, isReferralWithMultipleLocations });

      return data;
    } catch (error) {
      console.error('[AffiliateFlow] run failed:', error);
      return null;
    }
  }

  /**
   * Fetch affiliate data from API with two-tier caching (TTL: 15 minutes).
   * On TTL expiry or corruption the sessionStorage entry is purged and fresh data is fetched.
   */
  static async fetchAffiliateData(affiliateId) {
    if (this._fetchPromise) return this._fetchPromise;

    const storageKey = this.KEYS.AFFILIATE_LOCATIONS + '_' + affiliateId;
    const TTL_MS = 15 * 60 * 1000; // 15 minutes

    try {
      const cached = sessionStorage.getItem(storageKey);

      if (cached) {
        const { data, expiresAt } = JSON.parse(cached);

        if (data && Date.now() < expiresAt) {
          return data;
        }

        sessionStorage.removeItem(storageKey);
      }
    } catch (e) {
      console.warn('[AffiliateFlow] Corrupted sessionStorage data, re-fetching:', e);
      try {
        sessionStorage.removeItem(storageKey);
      } catch (_) {}
    }

    // API fetch — store the promise so any concurrent caller awaits the same request
    this._fetchPromise = (async () => {
      try {
        const res = await fetchData(STYKU_API.AFFILIATE_PARTNER_LOCATIONS(affiliateId));
        if (!res?.ok) return null;

        const data = await res.json();

        sessionStorage.setItem(storageKey, JSON.stringify({ data, expiresAt: Date.now() + TTL_MS }));
        sessionStorage.setItem(this.KEYS.SHOP_ACCESS_GRANTED, 'true');

        return data;
      } catch (e) {
        console.error('[AffiliateFlow] API error:', e);
        return null;
      } finally {
        this._fetchPromise = null;
      }
    })();

    return this._fetchPromise;
  }

  // Cache URLSearchParams access
  static get params() {
    return new URLSearchParams(window.location.search);
  }

  // Helper to get value from URL params or dataset with fallback
  static getParamOrData(paramKey, dataKey) {
    return this.params.get(paramKey) || document.querySelector('header')?.dataset[dataKey] || null;
  }

  // Affiliate ID can come from URL or dataset
  static getAffiliateId() {
    return this.getParamOrData(this.KEYS.PARAM_REFERRER, 'referrerId');
  }

  // Location ID can come from URL or dataset
  static getLocationId() {
    return this.getParamOrData(this.KEYS.PARAM_LOCATION_ID, 'locationId');
  }

  // QR code ID can come from URL or dataset
  static getQrCodeId() {
    return this.getParamOrData(this.KEYS.PARAM_QR_CODE_ID, 'qrCodeId');
  }

  static getSource() {
    return this.getParamOrData(this.KEYS.PARAM_TRACKING_SOURCE, 'trackingSource');
  }

  // Hide location-related sections/buttons for affiliate users
  static hideLocationElements() {
    document
      .querySelectorAll('.locations-section, .link-locations')
      .forEach((el) => el.classList.add('affiliate-hide'));
  }

  // Update "Book" or "Scan" buttons to point to pricing page for affiliate users
  static updateButtons() {
    const pricingUrl = PAGE_URLS?.PRICING || '/pricing';

    document.querySelectorAll('a[href*="location"]').forEach((btn) => {
      if (this.isBookScanButton(btn)) {
        btn.href = pricingUrl;
        btn.dataset.affiliateModified = 'true';
      }
    });
  }

  // Identify if a button is a "Book" or "Scan" button based on its text
  static isBookScanButton(el) {
    const text = el.textContent?.toLowerCase() || '';
    return text.includes('book') || text.includes('scan');
  }

  // Hide the mockup "Add to Cart" buttons that are shown to non-affiliate users on product pages
  static hideMockupButtons() {
    document.querySelectorAll('.mockup-button-block').forEach((el) => {
      el.style.display = 'none';
    });
  }

  // Show the actual form wrappers that contain the product forms for affiliate users
  static showFormWrappers() {
    document.querySelectorAll('.form-wrapper').forEach((el) => {
      el.classList.remove('hidden');
    });
  }

  // Redirect affiliate users to locations page if they try to access the locations page directly
  static redirectIfNeeded() {
    const path = window.location.pathname;
    const locationsPath = PAGE_URLS?.LOCATIONS || '/locations';
    const pricingUrl = PAGE_URLS?.PRICING || '/pricing';

    if (path.includes(locationsPath)) {
      window.location.replace(pricingUrl);
    }
  }

  // Set up event listeners for "Add to Cart" buttons in affiliate flow
  static setupATCButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.mockup-button-block .btn-atc');
      if (!btn) return;

      e.preventDefault();

      const form = btn.closest('.buy-now-button-block')?.querySelector('.form-wrapper');

      this.redirectToLocationPage(form?.dataset ?? {});
    });
  }

  // Redirect to locations page with product, variant, and selling plan info for affiliate users.
  static redirectToLocationPage({ sellingPlanId, productId, variantId } = {}) {
    const url = new URL(PAGE_URLS?.LOCATIONS || '/pages/locations', window.location.origin);

    if (productId && variantId) {
      url.searchParams.set(this.KEYS.PARAM_PRODUCT_ID, productId);
      url.searchParams.set(this.KEYS.PARAM_VARIANT_ID, variantId);

      if (sellingPlanId) {
        url.searchParams.set(this.KEYS.PARAM_SELLING_PLAN_ID, sellingPlanId);
      }
    }

    window.location.href = url.toString();
  }

  // Get redirect data (product, variant, selling plan) from URL params for affiliate users coming from product page
  static getRedirectDataFromSearchParams() {
    const { PARAM_PRODUCT_ID, PARAM_VARIANT_ID, PARAM_SELLING_PLAN_ID } = this.KEYS;

    const params = this.params;

    return {
      productId: params.get(PARAM_PRODUCT_ID),
      variantId: params.get(PARAM_VARIANT_ID),
      sellingPlanId: params.get(PARAM_SELLING_PLAN_ID),
    };
  }

  // Get previous order attributes from session storage to apply
  static getPreviousOrderAttributesFromSession() {
    const previousAttrs = sessionStorage.getItem(this.KEYS.PREVIOUS_ORDER_ATTRIBUTE);
    return previousAttrs ? JSON.parse(previousAttrs) : null;
  }

  // Reapply previous order attributes to the current session to maintain affiliate context across sessions
  static reapplyPreviousOrderAttributes() {
    const previousAttrs = this.getPreviousOrderAttributesFromSession();

    if (!previousAttrs) return;

    const source = previousAttrs[this.KEYS.PARAM_TRACKING_SOURCE];
    const locationId = previousAttrs[this.KEYS.PARAM_LOCATION_ID];
    const affiliateId = previousAttrs[this.KEYS.PARAM_REFERRER];

    const hasDirectAccess = source === this.KEYS.SOURCE_MOBILE || source === this.KEYS.SOURCE_UNITY_QR_CODE_SCAN;
    const isOrganicWithLocation = source === this.KEYS.SOURCE_ORGANIC && !!locationId;

    let locationCount = 0;
    if (affiliateId) {
      try {
        const cached = sessionStorage.getItem(this.KEYS.AFFILIATE_LOCATIONS + '_' + affiliateId);
        if (cached) {
          const { data } = JSON.parse(cached);
          locationCount = data?.locationCount ?? 0;
        }
      } catch (_) {}
    }

    // Referral users with a location ID but more than 1 location should see the form but not the locations section
    const isReferralWithMultipleLocations = source === this.KEYS.SOURCE_AFFILIATE && locationCount > 1;

    // Apply the same affiliate UI logic as on initial load based on the previous order attributes
    this.applyAffiliateUI({ hasDirectAccess, locationCount, isOrganicWithLocation, isReferralWithMultipleLocations });
  }

  /**
   * Apply affiliate-related UI changes based on user context:
   * @param {boolean} hasDirectAccess - Whether the user has direct access via mobile app or QR code scan
   * @param {number} locationCount - Number of locations associated with the affiliate (if applicable)
   * @param {boolean} isOrganicWithLocation - Whether the user is an organic visitor with a location ID
   * @param {boolean} isReferralWithMultipleLocations - Whether the user is a referral with a location ID and multiple locations available
   */
  static applyAffiliateUI({ hasDirectAccess, locationCount, isOrganicWithLocation, isReferralWithMultipleLocations }) {
    if (hasDirectAccess || locationCount === 1) {
      document.body.classList.add('affiliate-user');

      this.hideMockupButtons();
      this.showFormWrappers();
      this.hideLocationElements();
      this.updateButtons();
      this.redirectIfNeeded();
    } else if (isOrganicWithLocation || isReferralWithMultipleLocations) {
      document.body.classList.add('affiliate-user');

      this.hideMockupButtons();
      this.showFormWrappers();
    }
  }

  // Render affiliate location experience for referred users based on their affiliate data
  static ensureAffiliateLocationUI() {
    const affiliateId = this.getAffiliateId();

    // No affiliate ID means no referral context, so we don't need to show the banner
    if (!affiliateId) return;

    if (!document.getElementById('affiliate-location-banner')) {
      const banner = document.createElement('div');
      banner.id = 'affiliate-location-banner';

      banner.innerHTML = `
      <div class="affiliate-location-banner-inner text-primary-gradient container px-4 lg:px-0">
        <div id="affiliate-location-banner-text"></div>
      </div>
    `;

      const header = document.querySelector('header');

      if (header && header.parentNode) {
        header.parentNode.insertBefore(banner, header);
      } else {
        document.body.prepend(banner);
      }
    }
  }

  // Update the content of the affiliate banner with the names of the locations associated with the affiliate.
  static updateAffiliateBannerContent() {
    const bannerText = document.getElementById('affiliate-location-banner-text');
    if (!bannerText) return;

    const locations = this._affiliateData?.locations || [];

    // affiliate but NO locations
    if (locations.length === 0) {
      bannerText.textContent = 'This affiliate is not associated with any locations.';
      return;
    }

    const names = locations.map((loc) => loc.companyName).filter(Boolean);

    bannerText.innerHTML = `<span class="font-semibold">Affiliate link for:</span> ${this.formatNameList(names)}`;
  }

  /**
   * Format a list of names into a human-readable string with proper commas and "and" conjunction.
   * Examples:
   * - ["A"] => "A"
   * - ["A", "B"] => "A and B"
   * - ["A", "B", "C"] => "A, B, and C"
   */
  static formatNameList(list) {
    if (list.length === 1) return list[0];

    if (list.length === 2) return `${list[0]} and ${list[1]}`;

    return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
  }
}

// Expose globally for use in inline scripts and other modules
window.AffiliateFlow = AffiliateFlow;
AffiliateFlow.init();
